import { Container } from '@acies/core';
import { DropdownEvent, Icon, Label, Spacer } from '@acies/ui';
import type { StockConfiguration } from '../../../domain/configurations';
import { SearchResult } from '../../../domain/search-results';
import { listInventoryItemConfigurations } from '../../../services/inventory-item-configurations';
import { ConfiguratorModel } from '../configurator.model';
import { SCROLLBAR_WIDTH } from '../shared/scrollbar-width';
import ItemConfigurationDropdown from './item-configuration-dropdown';
import ItemConfigurationRendererEvent from './item-configuration-renderer-event';

/**
 * The row above the answers list where the configuration a stock position's item comes in is picked.
 *
 * Laid out as a row of the list is — see `AnswerItemRenderer` — so it reads as the first answer: an icon, a name, the dropdown in the value column, the type and the side left empty, and the status and the menu as the rows have them. Not a row of the list, because it answers to the stock configuration rather than to a
 * parameter of it, and a pick here is a new configuration with new parameters, so the rows below it are dealt again while it stays where it is.
 *
 * Shown only for a stock configuration that links the list its configurations are picked from — see `StockConfiguration.itemConfigurations` — and out of the layout otherwise, so the answers list starts right under the header for every other position.
 *
 * The right hand padding leaves room for the scrollbar the list always shows, the way the header does, so the columns end where the list's do.
 */
export default class ItemConfigurationRenderer extends Container {
	#dropdown!: ItemConfigurationDropdown;
	#icon!: Icon;
	#iconContainer!: Container;
	#menuIcon!: Icon;
	#nameLabel!: Label;
	/** The stock configuration the row is showing, or null while it shows none. */
	#shown: StockConfiguration | null;
	#sideSpacer!: Spacer;
	#statusContainer!: Container;
	#statusIcon!: Icon;
	#targetLabel!: Label;
	public constructor() {
		super();
		this.#shown = null;
		this.width = 'fill';
		this.height = 32;
		this.gapHorizontal = 16;
		this.paddingHorizontal = [16, 16 + SCROLLBAR_WIDTH];
		this.alignment = 'left';
		this.visible = false;
		this.configurationChanged = this.configurationChanged.bind(this);
		// Subscribed here rather than on connect, because an Observable tells its listeners about a
		// change and not about the value it already holds. What the model already holds is read on
		// connect, in initialize.
		ConfiguratorModel.currentStockConfiguration.subscribe(this.configurationChanged);
		this.addEventListener(DropdownEvent.SELECTED_CHANGED, this.#dropdownChanged as EventListener);
	}

	public get icon(): Icon {
		if (!this.#icon) {
			this.#icon = new Icon();
			this.#icon.name = 'settings-01';
		}
		return this.#icon;
	}

	/** The column the icon sits in, as wide as the one the answers list keeps for its own. See `AnswerItemRenderer.iconContainer`. */
	public get iconContainer(): Container {
		if (!this.#iconContainer) {
			this.#iconContainer = new Container();
			this.#iconContainer.width = 32;
			this.#iconContainer.height = 'fill';
			this.#iconContainer.alignment = 'center';
			this.#iconContainer.addComponent(this.icon);
		}
		return this.#iconContainer;
	}

	public get nameLabel(): Label {
		if (!this.#nameLabel) {
			this.#nameLabel = new Label();
			this.#nameLabel.text = 'Lagerkonfiguration';
			this.#nameLabel.width = 'fill';
		}
		return this.#nameLabel;
	}

	/** Where the configuration is picked, in the value column, offering the configurations listed at the link the stock configuration names. */
	public get dropdown(): ItemConfigurationDropdown {
		if (!this.#dropdown) {
			this.#dropdown = new ItemConfigurationDropdown();
			this.#dropdown.list = listInventoryItemConfigurations;
		}
		return this.#dropdown;
	}

	/** The type column, left empty: a stock item has no target for its configuration to be about. */
	public get targetLabel(): Label {
		if (!this.#targetLabel) {
			this.#targetLabel = new Label();
			this.#targetLabel.width = 64;
		}
		return this.#targetLabel;
	}

	/** The side column, kept empty, as wide as the side dropdown of the rows below. */
	public get sideSpacer(): Spacer {
		if (!this.#sideSpacer) {
			this.#sideSpacer = new Spacer();
			this.#sideSpacer.width = 72;
		}
		return this.#sideSpacer;
	}

	public get statusIcon(): Icon {
		if (!this.#statusIcon) {
			this.#statusIcon = new Icon();
			this.#statusIcon.name = 'check-circle';
		}
		return this.#statusIcon;
	}

	/** The status column, saying the same as the rows below for now. See `AnswerItemRenderer.statusContainer`. */
	public get statusContainer(): Container {
		if (!this.#statusContainer) {
			this.#statusContainer = new Container();
			this.#statusContainer.width = 48;
			this.#statusContainer.height = 'fill';
			this.#statusContainer.alignment = 'center';
			this.#statusContainer.addComponent(this.statusIcon);
		}
		return this.#statusContainer;
	}

	public get menuIcon(): Icon {
		if (!this.#menuIcon) {
			this.#menuIcon = new Icon();
			this.#menuIcon.name = 'dots-vertical';
		}
		return this.#menuIcon;
	}

	/**
	 * Shows the stock configuration of the position being worked on: the row with the item's configurations on offer and the one the configuration names picked, or no row at all for a configuration with nothing to pick.
	 *
	 * The configurations arrive after the row is shown, and the one named is picked once they have — unless the position being worked on has moved on by then, and the pick is the next configuration's to make.
	 */
	public configurationChanged(configuration: StockConfiguration | null): void {
		this.#shown = configuration;
		const source = configuration === null ? null : configuration.itemConfigurationsList;
		if (configuration === null || source === null) {
			this.visible = false;
			this.dropdown.clear();
			return;
		}
		this.visible = true;
		void this.#show(configuration, source);
	}

	protected override initialize(): void {
		super.initialize();
		this.addComponents([this.iconContainer, this.nameLabel, this.dropdown, this.targetLabel, this.sideSpacer, this.statusContainer, this.menuIcon]);
		this.configurationChanged(ConfiguratorModel.currentStockConfiguration.value);
	}

	/** Offers the configurations listed at `source`, and picks the one `configuration` names once they are there, if it is still the one on show. */
	async #show(configuration: StockConfiguration, source: string): Promise<void> {
		await this.dropdown.load(source);
		if (this.#shown === configuration) {
			this.dropdown.select(configuration.itemConfigurationKey);
		}
	}

	/**
	 * Answers a configuration picked in the dropdown, when it is not the one the stock configuration names.
	 *
	 * Compared against the configuration, because the row picks in the dropdown itself: it picks the one named each time it is shown a configuration, and the dropdown reports that pick like any other.
	 *
	 * Nothing picked is not a configuration either. Clicking the one already picked takes it off again, so the row puts the one named back rather than saying anything, the way the answer rows put an answer back — once the click that took it off is over. A configuration that names none has nothing to put back.
	 *
	 * The dropdown's event says nothing outside the row and is stopped either way: what leaves the row is the pick, said as an {@link ItemConfigurationRendererEvent}.
	 */
	#dropdownChanged(event: DropdownEvent): void {
		event.stopImmediatePropagation();
		const configuration = this.#shown;
		if (configuration === null) {
			return;
		}
		const named = configuration.itemConfigurationKey;
		if (!(event.value instanceof SearchResult)) {
			if (named !== null) {
				queueMicrotask(() => {
					this.dropdown.select(named);
				});
			}
			return;
		}
		if (event.value.key === named) {
			return;
		}
		this.dispatchEvent(new ItemConfigurationRendererEvent(configuration, event.value.key));
	}
}
customElements.define('cf-item-configuration-renderer', ItemConfigurationRenderer);
